import React, {
  useState, useContext, useCallback, useMemo,
} from 'react';
import { apiFetch } from 'services/apiFetch';
import * as auth from 'services/auth';
import { AuthProviderType, AuthProviderProps } from './types';

const Context = React.createContext({} as AuthProviderType);
const useAuthContext = ():AuthProviderType => useContext(Context);

const AuthProvider = ({ children }:AuthProviderProps):JSX.Element => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const checkAuthentication = useCallback(async () => {
    if (!auth.hasCredentials()) {
      setIsAuthenticated(false);
      return;
    }

    try {
      const response = await apiFetch('/session', { method: 'GET' });

      if (response.status === 200) {
        const data = await response.json() as { id: number, name: string, email: string };
        setEmail(data.email);
        setName(data.name);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    } catch {
      setIsAuthenticated(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch('/session', { method: 'DELETE' });
    } finally {
      auth.clearCredentials();
      setIsAuthenticated(false);
    }
  }, []);

  const value = useMemo(() => ({
    email,
    setEmail,
    name,
    setName,
    isAuthenticated,
    checkAuthentication,
    setIsAuthenticated,
    logout,
  }), [email, name, isAuthenticated, checkAuthentication, logout]);

  return (
    <Context.Provider value={value}>
      {children}
    </Context.Provider>
  );
};

export { AuthProvider, useAuthContext };
